import i18n from "i18next";

/**
 * Turns whatever the backend hands us into something a person can act on.
 *
 * Services are supposed to throw with an `error.*` translation key, and most
 * do. The rest leaks: a throw site that passed no message inherits its error
 * class's English default ("Unauthorized Error"), and a failed service-to-
 * service call arrives as a plain Error whose text embeds the internal URL.
 * Both were being rendered into the toast verbatim.
 *
 * Nothing technical reaches the user from here. The raw text still goes to the
 * console, which is where it is useful.
 */

// Transport-level failures: the platform is unreachable, which is never
// something the person filling in the form can fix by trying different input.
const TRANSPORT = /ECONNREFUSED|ECONNRESET|ETIMEDOUT|ENOTFOUND|EAI_AGAIN|EHOSTUNREACH|socket hang up|network\s*error|failed to fetch|->\s*Service\[/i;

// Defaults baked into @dehub-core's error classes, surfaced whenever a throw
// site omits the message argument.
const CLASS_DEFAULTS: Record<string, string> = {
  "Unauthorized Error": "error.auth.session_expired",
  "Forbidden Error": "error.auth.access_denied",
  "Notfound Error": "error.generic",
  "Validation Error": "error.generic",
  "Unknown error": "error.generic",
  SystemBusy: "error.generic",
  FATAL_ERROR: "error.generic",
};

const CYRILLIC = /[Ѐ-ӿ]/;

// i18next returns this sentence instead of throwing when a key resolves to a
// branch of the tree rather than a leaf. It must never reach a toast.
const isObjectComplaint = (value: string) => value.startsWith("key '") && value.includes("returned an object");

const translate = (key: string): string => {
  const value = i18n.t(key, { ns: "common", defaultValue: "" });
  if (typeof value !== "string" || !value || isObjectComplaint(value)) return "";
  return value;
};

const generic = () => translate("error.generic") || "Алдаа гарлаа. Дахин оролдоно уу.";

export const humanizeError = (raw?: string | null, code?: string): string => {
  const message = (raw ?? "").trim();
  if (!message) return generic();

  // A key the backend sent on purpose, and we have the copy for it.
  const translated = translate(message);
  if (translated) return translated;

  if (TRANSPORT.test(message)) {
    console.error("[api] transport failure:", message);
    return translate("error.network") || generic();
  }

  const mapped = CLASS_DEFAULTS[message];
  if (mapped) {
    console.error("[api] error class default leaked, throw site passed no message:", { message, code });
    return translate(mapped) || generic();
  }

  // An `error.*` key we have no copy for, or any other English/internal text.
  // Neither is readable, and guessing is worse than a clear generic message.
  if (message.startsWith("error.") || !CYRILLIC.test(message)) {
    console.error("[api] untranslated error:", { message, code });
    return generic();
  }

  // Already written in Mongolian for the user — pass it through untouched.
  return message;
};
